import moment from 'moment'
import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, OFFER_TYPE_VILLA } from 'constants/offer'
import { dateIsBefore } from 'lib/datetime/dateUtils'
import { isConnectionCancellationPolicy, isWithinChangeOfMindPolicy, isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import { CHANNEL_MANAGER_SYNXIS } from 'constants/reservation'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getBookingProtectionInsuranceOrderItem, getRefundProtectOrderItem, orderHasCancellationProtection } from 'lib/order/orderInsuranceUtils'
import { PAYMENT_METHODS_NOT_ALLOWED_REFUND } from 'constants/payment'
import { isBedbankOrderItem, isOrderItem } from 'lib/order/orderUtils'
import { hasApprovedBNBLRefundRequest } from 'lib/order/refundRequestUtils'
import { logNewRelic } from 'services/newRelic'
import equal from 'fast-deep-equal'

export const DEFAULT_PARTNER_POLICY_ALLOWED_DAYS_TO_REFUND_TO_CREDIT = 7

export interface OrderHotelCancellationConfig {
  /**
   * The type of policy this config is
   * Policy types:
   *
   * Refundable :
   * Standard refundable, this means it supports change of mind and
   * return to credit. Also has 14 day exclusion period prior to
   * check in
   *
   * Flexible:
   * Refundable, typical exclusion period of 14 days before check in
   * does not apply to these policies
   *
   * Non-refundable:
   * Cannot refund at all
   *
   * Hidden:
   * Can only refund within change of mind. These types of policies
   * are mostly visually hidden from the user.
   */
  policyType: App.CancellationConfigPolicyType;
  /**
   * The last date the user can cancel and get any kind of refund
   * For the specific type of refund, check other props
   */
  policyDeadline: string;
  /**
   * Whether or not the hotel can use  booking protection (refund protect)
   * to get a refund
   * Note: this is different to travel insurance cancellation protection
   */
  canUseBookingProtection: boolean;
  /**
   * Whether or not the hotel can use cancellation protection (through insurance)
   * to get a refund
   * Note: This is different to refund protect
   */
  canUseCancellationProtection: boolean;
  /**
   * Whether or not the user can process their own refund
   */
  canAutoRefund: boolean;
  /**
   * Whether or not the user can make a refund request
   */
  canRequestRefund: boolean;
  /**
   * Whether or not the current hotel can be refunded to the original payment method
   */
  canRefundToOrigin: boolean;
  /**
   * Whether or not the current hotel can be funded to brand credit
   */
  canRefundToCredit: boolean;
  /**
   * Whether or not this current hotel can be given a refund in any way
   * Note: This excludes making a refund request (as it's not an actual refund, yet) or
   * refunds through cancellation/booking protection
   */
  canRefund: boolean;
}
const autoRefundableOfferTypes = new Set<App.OfferType>([OFFER_TYPE_HOTEL, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA, OFFER_TYPE_BED_BANK])

function getConfigPolicyType(policy: App.OrderCancellationPolicyTypes): App.CancellationConfigPolicyType {
  if (isConnectionCancellationPolicy(policy)) {
    return policy.nonRefundable ? 'non-refundable' : 'refundable'
  } else {
    if (isFlexibleCancellationPolicy(policy)) {
      return 'flexible'
    } else if (policy.type === 'hidden-cancellation-policy') {
      return 'hidden'
    } else if (policy.type === 'non-refundable') {
      return 'non-refundable'
    } else {
      return 'refundable'
    }
  }
}

function isValidHotelType(item: App.OrderItem): boolean {
  return (item.offer.type === OFFER_TYPE_HOTEL && config.ENABLE_REFUND_FOR_FLASH_OFFER) ||
  item.offer.type === OFFER_TYPE_ALWAYS_ON
}

interface CancellationConfigOptions {
  roomId?: string;
}

function getHotelCancellationConfig(
  item: App.OrderItem | App.OrderBedbankItem,
  order: App.Order,
  options: CancellationConfigOptions = {},
): OrderHotelCancellationConfig {
  const activeRefundProtectItem = getRefundProtectOrderItem(order)
  const activeBookingProtectionInsuranceItem = getBookingProtectionInsuranceOrderItem(order)

  const configs: OrderHotelCancellationConfig = {
    policyType: 'non-refundable',
    policyDeadline: '',
    canUseBookingProtection: false,
    canUseCancellationProtection: false,
    canAutoRefund: false,
    canRequestRefund: false,
    canRefundToOrigin: false,
    canRefundToCredit: false,
    canRefund: false,
  }
  let isDynamicPolicy = false

  if (isOrderItem(item)) {
    // comped packages can never be refunded, we gave them for free!
    if (item.reservation && !item.isCompedPackage) {
      if ('ratePlanCancellationPolicy' in item.package) {
        isDynamicPolicy = item.package.ratePlanCancellationPolicy == 'dynamic'
      }
      configs.canUseBookingProtection = !!activeRefundProtectItem || !!activeBookingProtectionInsuranceItem

      const cancellationPolicy = item.reservation.cancellationPolicies[0]
      if (cancellationPolicy) {
        configs.policyType = getConfigPolicyType(cancellationPolicy)

        if (configs.policyType !== 'non-refundable') {
          if (isConnectionCancellationPolicy(cancellationPolicy)) {
            configs.policyDeadline = cancellationPolicy.deadline

            // Connection policies that offer $0 refund after deadline are considered non-refundable
            const nonRefundableAfterDeadline = !cancellationPolicy.amountPercent || (item.total === cancellationPolicy.amountPercent.sell)
            // connection policies become partially refundable after the deadline date
            // the amount they get back depends upon the polices configuration
            const isWithinPolicyDeadline = configs.policyDeadline ? dateIsBefore(new Date(), new Date(configs.policyDeadline)) : false
            configs.canRefundToOrigin = nonRefundableAfterDeadline ? isWithinPolicyDeadline : true
            configs.canRefundToCredit = nonRefundableAfterDeadline ? isWithinPolicyDeadline : true
          } else if (isValidHotelType(item)) {
            const exclusionPeriod = moment(item.reservation.startDate).subtract(14, 'days')
            if (configs.policyType === 'hidden') {
              // hidden policies don't have a start/end date, but always offer the change of mind
              // so we're going to make our policy deadline the end of the change of mind period
              configs.policyDeadline = moment.min(exclusionPeriod, moment(order.createdAt).add(7, 'days')).format(ISO_DATE_FORMAT)
            } else if (cancellationPolicy.start) {
              // LPC bookings should always use the policy start date as the policy deadline (no exclusion periods should apply)
              const ignoreChangeOfMindExclusionPeriod =
                cancellationPolicy.type === 'post-purchase-seven-days' ||
                configs.policyType === 'flexible' ||
                item.offer.type === OFFER_TYPE_ALWAYS_ON
              if (ignoreChangeOfMindExclusionPeriod) {
                configs.policyDeadline = cancellationPolicy.start
              } else {
                configs.policyDeadline = moment.min(exclusionPeriod, moment(cancellationPolicy.start)).format(ISO_DATE_FORMAT)
              }
            }

            const withinChangeOfMind = item.reservation ? isWithinChangeOfMindPolicy(order.createdAt, item.reservation.startDate) : false
            const isWithinPolicyDeadline = configs.policyDeadline ? dateIsBefore(new Date(), new Date(configs.policyDeadline)) : false
            // Refund to original available when:
            // - within change of mind (7 days) and not before 14 days of check in
            // - cancellation policy allows it and policy is not "credits only" type
            // - LPC offers should use the policy deadline
            configs.canRefundToOrigin = item.offer.type === OFFER_TYPE_ALWAYS_ON ? isWithinPolicyDeadline : withinChangeOfMind
            configs.canRefundToCredit = isWithinPolicyDeadline || withinChangeOfMind

            /**
             * Refund requests can be made up until the day before check-in, but any requests
             * made within 3 days of check-in will be auto-rejected after a brief delay.
            */
            const cancellationOutsidePolicyDeadline = moment(item.reservation.startDate).format(ISO_DATE_FORMAT)
            configs.canRequestRefund = configs.policyType !== 'hidden' && dateIsBefore(new Date(), new Date(cancellationOutsidePolicyDeadline))
          }

          const hasApprovedRefundRequest = order.refundRequests[item.id]?.status === 'approved'
          const isWithinPolicyDeadline = configs.policyDeadline ? dateIsBefore(new Date(), new Date(configs.policyDeadline)) : false
          configs.canUseCancellationProtection = (isWithinPolicyDeadline || hasApprovedRefundRequest) && orderHasCancellationProtection(order)
        }
      }
    }
  } else {
    const room = item.rooms.find(room => room.roomId === options.roomId) ?? item.rooms[0]
    const policy = room.cancellationPolicies.policies[0]

    configs.policyType = (room.refundable || room.partiallyRefundable) ? 'refundable' : 'non-refundable'
    configs.policyDeadline = policy?.start ? moment.utc(policy.start).toISOString() : ''

    const isWithinPolicyDeadline = configs.policyDeadline ? dateIsBefore(new Date(), new Date(configs.policyDeadline)) : false

    // Bedbank doesn't support refund requests
    configs.canRequestRefund = false
    // Bedbank always allows both refund types
    configs.canRefundToOrigin = isWithinPolicyDeadline
    configs.canRefundToCredit = isWithinPolicyDeadline

    configs.canUseBookingProtection = !!activeRefundProtectItem || !!activeBookingProtectionInsuranceItem
    configs.canUseCancellationProtection = isWithinPolicyDeadline && orderHasCancellationProtection(order)
  }
  // Disabled for gift orders for now. Will consult with team.
  if (
    isOrderItem(item) &&
    item.reservationType === 'buy_now_book_later' &&
    !item.reservationMade &&
    !order.orderGift &&
    (!item.offer.bookByDate || moment().isBefore(item.offer.bookByDate))
  ) {
    // taken from svc-order validateBNBLItemForCustomerRefund.
    configs.policyType = 'refundable'
    if (item.canBookDates) {
      configs.canRefundToCredit = true

      const timezoneOffset = item.offer?.property?.timezoneOffset
      const dayOrderPurchase = timezoneOffset ? moment(order.createdAt).utcOffset(timezoneOffset) : moment(order.createdAt)
      const backToOriginRefundDeadline = dayOrderPurchase.add(DEFAULT_PARTNER_POLICY_ALLOWED_DAYS_TO_REFUND_TO_CREDIT, 'days')
      const withinBackToOriginRefund = moment().isSameOrBefore(backToOriginRefundDeadline, 'day')

      configs.canRefundToOrigin = withinBackToOriginRefund
      configs.policyDeadline = moment(item.offer.bookByDate).format(ISO_DATE_FORMAT)
    }
  }

  /** if a customer has outside policy change dates request on the item it means
   * they've been outside policy and couldn't change dates or refund without vendor's approval
   * this ensures they won't be able to refund or date change without a request to vendor
   */
  const hasOutsidePolicyChangeDatesRequests = order.outsidePolicyDatesRequests[item.id] || order.outsidePolicyDatesRequests[item.downgradedFromId ?? '']

  configs.canRequestRefund = !hasApprovedBNBLRefundRequest(order, item) && configs.canRequestRefund
  configs.canRefund = !hasApprovedBNBLRefundRequest(order, item) && !hasOutsidePolicyChangeDatesRequests && (configs.canRefundToCredit || configs.canRefundToOrigin)
  const creditsOnlyCancellation = !configs.canRefundToOrigin && configs.canRefundToCredit

  const hasOutsidePolicyRequests = hasApprovedBNBLRefundRequest(order, item) || hasOutsidePolicyChangeDatesRequests

  // Only certain offer types can auto refund
  configs.canAutoRefund = !hasOutsidePolicyRequests && autoRefundableOfferTypes.has(item.offer.type) &&
  // make sure we're allowed to refund synxis offers, they are a special channel manager
  (config.ENABLE_REFUND_FOR_SYNXIS_OFFER || (isOrderItem(item) && item.reservation?.channelManager !== CHANNEL_MANAGER_SYNXIS)) &&
  // can't refund orders with flights on them, business rule
  !order.flightItems.length &&
  // refunding is done by the insurance provider, can't automatically do it
  !activeRefundProtectItem?.refundInitiatedFromProvider &&
  // finally, if it's only credits we good - otherwise just make sure the payment methods the user used is allowed
  (creditsOnlyCancellation || !order.payments.some(payment => PAYMENT_METHODS_NOT_ALLOWED_REFUND.includes(payment.type))) &&
  // can't refund Villas with dynamic cancellation policy
  !(item.offer.type === OFFER_TYPE_VILLA && isDynamicPolicy)

  /* Shadow testing
  ** This compares cancellationConfig returned from svc-order with the return of this function
  ** If they don't match it will report an error to sentry so we can investigate all possible cases
  ** At the end we will deprecated this function and use svc-order's cancellationConfig
  */
  if (isOrderItem(item)) {
    if (item.cancellationConfig && !equal(configs, item.cancellationConfig)) {
      logNewRelic('Cancellation config mismatch', {
        orderId: order?.id,
        itemId: item?.id,
        itemConfigs: item.cancellationConfig,
        configs,
      })
    }
  }
  if (isBedbankOrderItem(item)) {
    const room = item.rooms.find(room => room.roomId === options.roomId) ?? item.rooms[0]
    if (room.cancellationConfig && !equal(configs, room.cancellationConfig)) {
      logNewRelic('Cancellation config mismatch [bedbank]', {
        orderId: order?.id,
        itemId: item?.id,
        roomId: room.roomId,
        itemConfigs: room.cancellationConfig,
        configs,
      })
    }
  }
  return configs
}

export default getHotelCancellationConfig
